import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { useMiniAppApi } from '../../hooks';
import type { MiniAppActionState, MiniAppContext } from '../../types';

import { CLINICAL_FINDINGS_AGENT_ID } from './constants';
import { getFindingsVersion, subscribeFindings } from './findings-refresh';

export const FINDINGS_POLL_INTERVAL_MS = 15_000;
export const FINDINGS_POLL_CAP_MS = 120_000;

/**
 * Drives the clinical-findings Review button from live state. Extraction runs
 * automatically on appointment completion, so there is no manual Extract action;
 * the Review button appears once findings are awaiting review and carries the
 * pending count.
 *
 *  - **0 pending** → hide Review (nothing to review yet, or all reviewed).
 *  - **≥1 pending** → show **Review** with the count.
 *  - **loading** → hide, to avoid a flash before the count is known.
 *
 * Re-fetches whenever the session changes or `bumpFindings()` fires (after a
 * submitted review), and polls (bounded) until the first finding appears, so the
 * button shows up once extraction finishes with the tab open, without a reload.
 */
export function useFindingsActionState(
  ctx: MiniAppContext,
): MiniAppActionState {
  const { tenantId, appointmentId } = ctx;
  const api = useMiniAppApi(tenantId, CLINICAL_FINDINGS_AGENT_ID);
  const version = useSyncExternalStore(subscribeFindings, getFindingsVersion);

  // null = not yet loaded for the current session/version.
  const [pending, setPending] = useState<number | null>(null);
  // Appointment whose findings have appeared; gates polling so a review that
  // empties the list never restarts it.
  const seenForAppointmentRef = useRef<string | null>(null);

  useEffect(() => {
    if (!appointmentId) {
      setPending(null);
      return;
    }

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let capId: ReturnType<typeof setTimeout> | undefined;

    const stopPolling = () => {
      clearInterval(intervalId);
      clearTimeout(capId);
    };

    const fetchOnce = async () => {
      try {
        const res = await api.listFindings(appointmentId);
        if (cancelled) return;
        const count = res.status === 200 ? res.body.findings.length : 0;
        setPending(count);
        if (count > 0) {
          seenForAppointmentRef.current = appointmentId;
          stopPolling();
        }
      } catch {
        if (!cancelled) setPending(0);
      }
    };

    setPending(null);
    void fetchOnce();

    if (seenForAppointmentRef.current !== appointmentId) {
      intervalId = setInterval(
        () => void fetchOnce(),
        FINDINGS_POLL_INTERVAL_MS,
      );
      capId = setTimeout(stopPolling, FINDINGS_POLL_CAP_MS);
    }

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [api, appointmentId, version]);

  const loaded = pending !== null;
  const count = pending ?? 0;
  return {
    review: { hidden: !loaded || count === 0, badge: count },
  };
}
